import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RefreshCw, Upload } from 'lucide-react';
import {
  PreflightCopy,
  ReportStatus,
  WarningDisposition,
} from '@signalvote/shared';
import type {
  AmendmentModel,
  EffectivePreviewDto,
  FilingAttestationRequest,
  FilingPreviewDto,
  FormatKind,
  PreflightWarning,
  WarningDispositionInput,
} from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import {
  useFileReport,
  useFilingPreflight,
  useFilingPreview,
  usePreviewWithDispositions,
  FileReportAttestationError,
  FileReportUnconfirmedContributionsError,
  FileReportPortalNoPackageError,
} from '@/hooks/queries/useFinanceReports';
import { formatAmount, formatDate } from '../utils';
import { triggerBrowserDownload } from './downloadHelpers';
import { portalNameFor } from './amendmentWorkflowHint';
import { AttestationModal } from './AttestationModal';
import { HeadsUpCard } from './HeadsUpCard';
import { HardFailureList } from './HardFailureList';
import { FecScheduleDDeferredDisclosure } from './FecScheduleDDeferredDisclosure';

// ============================================================================
// Props
// ============================================================================

interface FilePreflightModalProps {
  open: boolean;
  reportId: string;
  /** The report's token as this page read it; a stale one makes the server refuse the file (409). */
  concurrencyVersion: number;
  /** Closes the modal — caller still controls open state. */
  onClose: () => void;
  /** Called after a successful file — caller can navigate away. */
  onFiled?: (model: AmendmentModel | null) => void;
  /** Optional explicit format selection — passed through to POST /file. */
  formatOverride?: FormatKind | null;
  /**
   * #482 — federal download-and-self-upload: generating the `.fec` embeds the treasurer's
   * certification (so this attestation review still runs) but does NOT file it — the report stays
   * Ready and the treasurer uploads the file to efo.fec.gov, then marks it filed. When true the
   * primary action is worded "Certify and download", not "Confirm and File".
   */
  nonFinalizing?: boolean;
}

/**
 * TurboTax-style pre-flight review modal that gates the File Report
 * button. Renders:
 * <ul>
 *   <li>Itemized contributions block (Confirmed-only count + total).</li>
 *   <li>Expenditure block, grouped by category and payee.</li>
 *   <li>FPD-005 Heads-up card listing per-warning treasurer actions
 *       (Resolve / Attest / Dismiss) above the Confirm button.</li>
 * </ul>
 *
 * Labels are jurisdiction-neutral ("Itemized contributions", "Expenditures") —
 * not FEC-form-specific. The submission builder for each state renders the
 * correct line types per spec; this UI uses neutral language regardless.
 *
 * Per ADR-007 § Operating principle #1 (alert-don't-block), the server
 * never refuses the file on a warning — only on a hard failure ("XML
 * literally cannot emit"). FPD-004 retired the PPR-009 "Defer all" escape
 * hatch in favor of per-warning attestation through the attestation modal.
 */
export function FilePreflightModal({
  open,
  reportId,
  concurrencyVersion,
  onClose,
  onFiled,
  formatOverride,
  nonFinalizing = false,
}: FilePreflightModalProps) {
  const navigate = useNavigate();
  const { data: preview, isLoading, error, refetch } = useFilingPreview(open ? reportId : null);
  const { data: preflight, refetch: refetchPreflight } = useFilingPreflight(open ? reportId : null);
  const { fileReport } = useFileReport();
  const { previewWithDispositions } = usePreviewWithDispositions();

  const [isFiling, setIsFiling] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  // §25 — soft, non-alarming note shown in place of the red File error when a
  // still-Ready report's transactions were already uploaded via Export (so there's
  // no in-system package to generate). Distinct state from fileError so it renders
  // muted, not destructive-red.
  const [portalNoPackageNote, setPortalNoPackageNote] = useState<string | null>(null);
  // FPD-002: attestation modal opens when the treasurer clicks "Confirm and
  // File" on the preflight. The attestation payload (typed name + warning
  // dispositions) is collected before the POST goes out.
  const [attestationOpen, setAttestationOpen] = useState(false);
  const [attestationError, setAttestationError] = useState<string | null>(null);

  // FPD-005: per-warning treasurer choices accumulate here. Keyed by warningId.
  const [dispositions, setDispositions] = useState<Record<string, WarningDispositionInput>>({});

  // FPD-020: effective filed counts (Confirmed + attested-Pending minus
  // status-drift) refresh whenever the disposition set changes — the modal
  // surfaces the count the file-time builder will actually ship.
  const [effective, setEffective] = useState<EffectivePreviewDto | null>(null);
  useEffect(() => {
    if (!open || !preview) {
      setEffective(null);
      return;
    }
    let cancelled = false;
    const dispositionList = Object.values(dispositions);
    void previewWithDispositions({
      reportId,
      warningDispositions: dispositionList,
    }).then((result) => {
      if (!cancelled) setEffective(result);
    }).catch(() => {
      if (!cancelled) setEffective(null);
    });
    return () => { cancelled = true; };
  }, [open, preview, dispositions, previewWithDispositions, reportId]);

  const warnings = preflight?.warnings ?? [];
  const hardFailures = preflight?.hardFailures ?? [];
  const isFiled = preview?.status === ReportStatus.Filed;
  // FEC-013 — federal-only Schedule D deferral disclosure. `preview.stateCode`
  // is the file-time jurisdiction (FIN-011 `DefaultFormatStateCode ?? RulesetCode`),
  // so it matches the builder's `("federal", FecFile)` dispatch key. Non-federal
  // (AL/GA/LA/VA) filings never surface it — Schedule D is an FEC concept.
  const isFederal = preview?.stateCode?.toLowerCase() === 'federal';
  // ADR-007 § Operating principle #1 (alert-don't-block): Confirm is only
  // disabled when XML cannot literally emit (hard failures) or while a
  // request is in flight. Unaddressed warnings DO NOT block the click —
  // submit auto-dismisses anything the treasurer hasn't disposed of, and
  // the audit log captures both the warning shown and the implicit
  // dismissal so the reconciliation report can keep surfacing the
  // condition indefinitely.
  const hasHardFailure = hardFailures.length > 0;
  // §G — a portal-action-required warning (refund return, major contribution,
  // loan portal review, un-emittable in-kind "Other") carries a time-sensitive
  // portal obligation the file can't emit. These no longer silently auto-dismiss
  // at submit (they re-surface), and now the file itself can't quietly advance:
  // the treasurer must make an explicit per-warning choice — "Confirm and file
  // anyway" (Attest) or "Not applicable" (Dismiss) — before Confirm enables. This
  // is deliberately NARROWER than a blanket block: soft advisories still never
  // block the click (ADR-007 alert-don't-block), only this mandatory-handoff set.
  const undisposedPortalActionWarnings = warnings.some(
    (w) => (w.portalActionRequired ?? false) && !dispositions[w.warningId],
  );
  const disableConfirm =
    !preview || hasHardFailure || isFiling || undisposedPortalActionWarnings;

  function handleConfirm() {
    if (!preview) return;
    setFileError(null);
    setPortalNoPackageNote(null);
    setAttestationError(null);
    setAttestationOpen(true);
  }

  async function handleAttestationConfirm(attestation: FilingAttestationRequest) {
    if (!preview) return;
    setIsFiling(true);
    setAttestationError(null);
    try {
      // ADR-007 alert-don't-block: unaddressed warnings auto-dismiss at
      // submit so the click never refuses. The audit log captures every
      // warning shown (`dismissedWarnings` on `SubmissionPackageSigned`)
      // and the reconciliation report surfaces the unresolved condition
      // indefinitely.
      //
      // RFR-005 — the refund-return warning is NON-AUTO-DISMISSABLE: if the
      // treasurer didn't explicitly Attest or Dismiss it, send NO disposition
      // so the refund re-surfaces on the next preflight (the "can't silently
      // fall through" guarantee) instead of being silently auto-dismissed. The
      // File button still stays enabled — alert, don't block.
      const autoDispositions: WarningDispositionInput[] = warnings
        .map((w): WarningDispositionInput | null => {
          const chosen = dispositions[w.warningId];
          if (chosen) return chosen;
          // AFC-005 — the Major Contribution alert is non-auto-dismissable for the
          // same reason as the refund return: a $20k+ contribution/loan carries a
          // time-sensitive portal obligation that must not silently fall through, so
          // send NO disposition and let it re-surface on the next preflight.
          // AFC-008 (Part B) — the loan portal-review alert is likewise
          // non-auto-dismissable: every in-scope loan needs the treasurer to verify the
          // imported amount + add guarantors in the portal, so send NO disposition and
          // let it re-surface on the next preflight if untouched.
          // ADR-025 — the mandatory portal-handoff set (refund return, major contribution,
          // loan portal review, un-emittable In-Kind "Other") is a per-warning WIRE FLAG
          // the server sets, so this guard and the HeadsUp card's "Portal actions required"
          // grouping read the SAME flag and can never drift. A portal-action warning the
          // treasurer didn't explicitly dispose of gets NO auto-dismissal — it re-surfaces
          // on the next preflight rather than silently falling through.
          if (w.portalActionRequired ?? false) return null;
          return {
            warningId: w.warningId,
            warningType: w.warningType,
            warningTextShown: w.warningText,
            disposition: WarningDisposition.Dismissed,
            reason: null,
            expectedResolutionBy: null,
            exceptionTypeClassifier: null,
            contributionId: null,
          };
        })
        .filter((d): d is WarningDispositionInput => d != null);
      const merged: FilingAttestationRequest = {
        ...attestation,
        warningDispositions: autoDispositions,
        // Echo the session so the server checks each attested contribution
        // against the warning it was shown under.
        preflightSessionId: preflight?.preflightSessionId ?? null,
      };
      const result = await fileReport({
        reportId,
        data: {
          formatOverride: formatOverride ?? null,
          attestation: merged,
          concurrencyVersion,
        },
      });
      triggerBrowserDownload(result.zipBlob, result.fileName);
      onFiled?.(result.amendmentModel);
      setAttestationOpen(false);
      onClose();
    } catch (e) {
      if (e instanceof FileReportUnconfirmedContributionsError) {
        // FPD-005 retired the legacy unconfirmed-contributions hard block.
        // Defense-in-depth: if a server rollback re-introduces it, surface
        // the count via the regular file-error banner and re-fetch preview
        // + preflight so the modal reflects current state.
        setFileError(
          `${e.count} Pending contribution${e.count === 1 ? '' : 's'} blocked this filing. `
          + 'Refresh the modal and address them before re-trying.',
        );
        setAttestationOpen(false);
        await Promise.all([refetch(), refetchPreflight()]);
      } else if (e instanceof FileReportAttestationError) {
        // Typed-name mismatch / missing reason — keep the attestation
        // modal open with the server-provided message.
        setAttestationError(e.message);
      } else if (e instanceof FileReportPortalNoPackageError && !isFiled) {
        // §25 — export-confirm can leave a still-Ready report whose transactions
        // were already uploaded via Export; File then has no in-system package to
        // generate. Show the soft explanatory note (not the alarming red error);
        // the real correction is a portal amendment. NOTE: a still-Ready badge here
        // means export-confirm left the report thinking it's filed — a deeper
        // state-machine seam flagged for a follow-up issue, not fixed here.
        setPortalNoPackageNote(
          PreflightCopy.FilePortalNoPackageSoftNote.replace(
            '{portal}', portalNameFor(preview.stateCode),
          ),
        );
        setAttestationOpen(false);
      } else {
        setFileError(e instanceof Error ? e.message : 'Failed to file report');
        setAttestationOpen(false);
      }
    } finally {
      setIsFiling(false);
    }
  }

  // Header title varies by state per AC §Modal state matrix. The Amend prefix
  // is derived from preview.isAmendment, not from the report name — name
  // string contracts ("Amend: ...") are brittle and a future amend flow that
  // doesn't rename would silently regress the header.
  const headerTitle = (() => {
    if (!preview) return 'Review filing';
    if (isFiled) {
      return `Already filed${preview.filedDate ? ` on ${formatDate(preview.filedDate)}` : ''}`;
    }
    const verb = preview.isAmendment ? 'Amend' : 'Review';
    return `${verb}: ${preview.name}`;
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* §C — bound the modal to the viewport and make the BODY scroll while the
          footer stays pinned. Without this the review card grows taller than the
          window, cutting off the top and pushing "Confirm and File" below the
          fold (unreachable by mouse-wheel or at large zoom/font). */}
      <DialogContent className="flex max-h-[90vh] flex-col gap-0">
        <DialogHeader>
          <DialogTitle>{headerTitle}</DialogTitle>
          <DialogDescription>
            Confirm the figures below before generating the submission package. Server-side
            checks run on file — UI bypass is not possible.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body: everything between the header and the pinned footer.
            `min-h-0` lets the flex child shrink below its content so it can scroll. */}
        <div
          data-testid="preflight-body"
          className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1"
        >
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading filing preview…</div>
        )}

        {error && (
          <div className="space-y-2">
            <div
              className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="preflight-error"
            >
              Couldn't load the filing preview. There was an error fetching the
              data.
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              data-testid="preflight-retry-button"
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Retry
            </Button>
          </div>
        )}

        {preview && !error && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground" data-testid="preflight-period">
                Filing period: {formatDate(preview.periodStart)} – {formatDate(preview.periodEnd)}
              </p>
              {!isFiled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Refetch BOTH queries: the hard-failures/warnings render
                    // from the preflight query, so refetching only the preview
                    // would leave a since-fixed failure stale on screen.
                    void refetch();
                    void refetchPreflight();
                  }}
                  data-testid="preflight-refresh-button"
                  title="Refresh review"
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Refresh review
                </Button>
              )}
            </div>

            {preview.isAmendment && !isFiled && (
              <div
                className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900"
                data-testid="preflight-amendment-note"
              >
                This amendment will replace your previously filed report.
              </div>
            )}

            {/* FEC-013 — persistent Schedule D deferral disclosure on the
                federal filing/preview surface (alert-don't-block; no
                computation). Rendered before the figures so it's prominent. */}
            {!isFiled && isFederal && <FecScheduleDDeferredDisclosure />}

            {!isFiled && (
              <div className="space-y-3 rounded-md border bg-muted/30 p-3 text-sm">
                <div data-testid="preflight-itemized">
                  <p className="font-medium">Itemized contributions</p>
                  {/* FPD-020 — Confirm-filing modal renders the EFFECTIVE
                       count (Confirmed + attested-Pending) when an
                       attestation has been recorded, so what the treasurer
                       sees is what the file actually contains. */}
                  {effective && effective.attestedPendingCount > 0 ? (
                    <p
                      className="text-muted-foreground"
                      data-testid="preflight-itemized-effective"
                    >
                      {effective.contributions.count} contribution{effective.contributions.count === 1 ? '' : 's'} ·{' '}
                      {formatAmount(effective.contributions.sumAmount)} total — includes{' '}
                      {effective.attestedPendingCount} attested pending
                    </p>
                  ) : (
                    // Issue #146 — the pre-attestation fallback reads the will-file
                    // projection (`preview.willFile`), the SAME source the effective
                    // count reads, so the number never flickers between two
                    // computations as dispositions load.
                    <p className="text-muted-foreground">
                      {(effective?.contributions.count ?? preview.willFile.count)} contribution{(effective?.contributions.count ?? preview.willFile.count) === 1 ? '' : 's'} ·{' '}
                      {formatAmount(effective?.contributions.sumAmount ?? preview.willFile.total)} total
                    </p>
                  )}
                  {effective && effective.excludedDueToStatusDrift > 0 && (
                    <p
                      className="text-xs text-amber-800"
                      data-testid="preflight-status-drift"
                    >
                      {PreflightCopy.AttestedExcludedDueToStatusDrift.replace(
                        '{N}', String(effective.excludedDueToStatusDrift))}
                    </p>
                  )}
                </div>
                <div data-testid="preflight-expenditures">
                  <p className="font-medium">
                    Expenditures
                  </p>
                  {preview.expenditures.groups.length === 0 ? (
                    <p className="text-muted-foreground">No expenditures in this period.</p>
                  ) : (
                    <ul className="text-muted-foreground">
                      {preview.expenditures.groups.map((g) => (
                        <li key={`${g.category}|${g.contactName}`}>
                          {g.count} {g.count === 1 ? 'expenditure' : 'expenditures'} to {g.contactName} ·{' '}
                          {formatAmount(g.total)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* The bottom line of the filing, and the only figure on this screen no other
                    surface can give the treasurer: the report page and the filing preview both
                    render the COMMITTED state, so neither can answer "what is my ending cash if
                    I file this right now, with these attestations". It moves as warnings above
                    are attested. Shown as a footing bridge rather than a bare number so it
                    checks itself — beginning + received − paid must land on ending. */}
                {effective && (
                  <div className="border-t pt-3" data-testid="preflight-cash-on-hand">
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="font-medium">Cash on hand at close</p>
                      <p
                        className={`font-medium ${effective.cashOnHand.ending < 0 ? 'text-destructive' : ''}`}
                        data-testid="preflight-cash-on-hand-ending"
                      >
                        {formatAmount(effective.cashOnHand.ending)}
                      </p>
                    </div>
                    <p className="text-muted-foreground">
                      {formatAmount(effective.cashOnHand.beginning)} beginning +{' '}
                      {formatAmount(effective.cashOnHand.receipts)} received −{' '}
                      {formatAmount(effective.cashOnHand.disbursements)} paid
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* AFU-006 — the hard-failure list is now a shared read-only
                component so the close hub (CloseHubPreflight) renders the
                identical block before the File modal and the two can't drift. */}
            {!isFiled && <HardFailureList hardFailures={hardFailures} />}

            {!isFiled && (
              <HeadsUpCard
                warnings={warnings}
                dispositions={dispositions}
                onChange={setDispositions}
                onResolve={(warning) => {
                  // USI-005 (ADR-011): warnings deep-link to the one-phase close hub.
                  //
                  // GitHub #61 — when the SERVER named a row to cure and an amount to propose
                  // (today the over-limit warning), go one step further and open the cure dialog
                  // on that row with the amount pre-filled. Otherwise the treasurer is told to
                  // redesignate or reattribute the excess and then has to find the gift and work
                  // out the arithmetic themselves, against a 60-day deadline. Both figures come
                  // from the warning as emitted — nothing about a limit is derived here.
                  const targetRowId = warning.resolveTargetRowId;
                  void navigate({
                    to: '/admin/finance/close',
                    search: targetRowId
                      ? {
                          reportId,
                          action: 'refund',
                          contributionId: targetRowId,
                          excess: warning.resolveTargetAmount ?? undefined,
                        }
                      : { reportId },
                  } as never);
                }}
              />
            )}

            {fileError && (
              <div
                className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive"
                data-testid="preflight-file-error"
              >
                {fileError}
              </div>
            )}

            {/* §25 — soft explanation (not the alarming red error) when File has no
                in-system package because the transactions were already uploaded via
                Export. Muted amber, not destructive. */}
            {portalNoPackageNote && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
                data-testid="preflight-file-portal-no-package"
              >
                {portalNoPackageNote}
              </div>
            )}
          </>
        )}
        </div>

        <DialogFooter className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isFiling}>
            Cancel
          </Button>
          {isFiled && preview?.latestSubmissionPackageId && (
            <a
              href={`/admin/finance/reports/${preview.reportId}`}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              data-testid="preflight-view-receipt"
            >
              View receipt
            </a>
          )}
          {!isFiled && (
            <PendingButton
              pending={isFiling}
              icon={<Upload className="h-4 w-4" />}
              onClick={handleConfirm}
              disabled={disableConfirm}
              data-testid="preflight-confirm-and-file"
            >
              {isFiling
                ? (nonFinalizing ? 'Generating…' : 'Filing…')
                : (nonFinalizing ? 'Certify and download' : 'Confirm and File')}
            </PendingButton>
          )}
        </DialogFooter>
      </DialogContent>

      {/* FPD-002: the attestation modal mounts only when the treasurer
          clicks Confirm. Conditional render (vs. always-mounted with
          `open` toggling) keeps the modal's useAuth subscription out of
          the preflight's render path — preserves existing test fixtures
          that don't wrap in AuthProvider. */}
      {attestationOpen && (
        <AttestationModal
          open={attestationOpen}
          preview={preview ?? null}
          onClose={() => setAttestationOpen(false)}
          onConfirm={handleAttestationConfirm}
          isFiling={isFiling}
          errorMessage={attestationError}
        />
      )}
    </Dialog>
  );
}
